import React, { useState, useCallback, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress } from "reactstrap";
import { useDropzone } from "react-dropzone";

// Import SVG icons
import { ReactComponent as ImageIcon } from "assets/icons/image.svg";
import { ReactComponent as DownloadIcon } from "assets/icons/download.svg";
import { ReactComponent as TrashIcon } from "assets/icons/trash.svg";

const UploadImage = ({
  isOpen,
  toggle,
  initFile = null, // Single file URL or File object
  title = "อัปโหลดรูปภาพ",
  desc = "อัปโหลดรูปภาพได้ที่ด้านล่างนี้",
  message = "คลิกเพื่อเลือกหรือลากไฟล์มาวางที่นี่",
  accept = "PNG, JPG, JPEG, WEBP",
  maxSize = 5 * 1024 * 1024, // 5MB default
  onUploadFile = null,
  onRemoveFile = null,
  onConfirm,
  onCancel,
  immediateAction = false, // New prop to control immediate actions
}) => {
  const [file, setFile] = useState(() => {
    if (!initFile) return null;
    return {
      id: "initial",
      url: typeof initFile === "string" ? initFile : URL.createObjectURL(initFile),
      name: typeof initFile === "string" ? initFile.split("/").pop() : initFile.name,
      size: typeof initFile === "string" ? "" : formatFileSize(initFile.size),
      status: "completed",
      progress: 100,
      isNew: false,
      originalFile: initFile
    };
  });
  const [pendingRemoval, setPendingRemoval] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [removedFile, setRemovedFile] = useState(null);

  // Handle file changes
  useEffect(() => {
    if (immediateAction && file?.isNew && onUploadFile) {
      handleUpload(file);
    }
  }, [file, immediateAction]);

  const handleFile = (newFile) => {
    // Check file size
    if (newFile.size > maxSize) {
      alert(`ไฟล์ต้องมีขนาดไม่เกิน ${formatFileSize(maxSize)}`);
      return;
    }

    const fileData = {
      id: Math.random().toString(36).substr(2, 9),
      file: newFile,
      name: newFile.name,
      size: formatFileSize(newFile.size),
      progress: immediateAction ? 0 : 100,
      status: immediateAction ? "uploading" : "completed",
      url: URL.createObjectURL(newFile),
      isNew: true,
    };

    setFile(fileData);
  };

  const handleDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      handleFile(acceptedFiles[0]);
    }
  }, []);

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
      e.target.value = null;
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/*": accept.split(",").map((type) => `.${type.trim().toLowerCase()}`),
    },
    maxFiles: 1,
    maxSize: maxSize,
    noClick: true,
    onDropRejected: (rejectedFiles) => {
      rejectedFiles.forEach((rejection) => {
        if (rejection.errors[0].code === "file-too-large") {
          alert(`ไฟล์ ${rejection.file.name} มีขนาดใหญ่เกินไป (สูงสุด ${formatFileSize(maxSize)})`);
        } else if (rejection.errors[0].code === "file-invalid-type") {
          alert(`รองรับเฉพาะไฟล์ประเภท: ${accept}`);
        }
      });
    },
  });

  const handleUpload = async (fileData) => {
    if (!fileData || !fileData.file || !onUploadFile) return;
    
    setIsUploading(true);
    setError(null);
    
    try {
      const onProgress = (progress) => {
        setFile(prev => ({
          ...prev,
          progress,
          status: progress < 100 ? "uploading" : "completed"
        }));
      };

      const url = await onUploadFile(fileData.file, onProgress);
      
      setFile(prev => ({
        ...prev,
        progress: 100,
        status: "completed",
        url: url || prev.url,
        isNew: false,
        originalFile: url || prev.file
      }));
      
      return url || fileData.url;
    } catch (error) {
      console.error("Upload error:", error);
      setError("อัพโหลดไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      setFile(prev => ({
        ...prev,
        status: "error",
        error: "อัพโหลดไม่สำเร็จ"
      }));
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    if (!file) return;
    
    // Store the current file for potential server deletion on confirm
    const fileToRemove = { ...file };
    
    // Clear the current file from the UI
    setFile(null);
    
    // Set pending removal state and store the file that was removed
    setPendingRemoval(true);
    setRemovedFile(fileToRemove);
    setError(null);
  };

  const handleConfirm = async () => {
    if (isUploading) return;

    try {
      // If we have a removed file, call onRemoveFile if it exists
      if (removedFile && onRemoveFile) {
        await onRemoveFile(removedFile);
      }
      
      // Handle file upload if we have a file
      let result = null;
      if (file) {
        if (!file.isNew && file.status === 'completed') {
          result = file;
        } else if (file.isNew || file.status !== 'completed') {
          const url = await handleUpload(file);
          if (url) {
            result = {
              ...file,
              url,
              isNew: false,
              status: 'completed'
            };
          }
        }
      }

      // Call the confirm callback with the final file state
      if (onConfirm) {
        onConfirm(result);
      }
      
      // Close the modal if not in immediate action mode
      if (!immediateAction) {
        toggle();
      }
    } catch (error) {
      console.error('Error during confirmation:', error);
      setError('เกิดข้อผิดพลาดในการยืนยัน กรุณาลองใหม่อีกครั้ง');
    }
  };

  const handleCancel = () => {
    // Reset any pending changes
    if (pendingRemoval && initFile) {
      setFile({
        id: "initial",
        url: typeof initFile === "string" ? initFile : URL.createObjectURL(initFile),
        name: typeof initFile === "string" ? initFile.split("/").pop() : initFile.name,
        size: typeof initFile === "string" ? "" : formatFileSize(initFile.size),
        status: "completed",
        progress: 100,
        isNew: false,
        originalFile: initFile
      });
      setPendingRemoval(false);
    } else if (file?.url && file.url.startsWith('blob:')) {
      // Clean up any blob URLs
      URL.revokeObjectURL(file.url);
    }
    
    // Call the onCancel callback if provided, otherwise use toggle
    if (onCancel) {
      onCancel();
    } else {
      toggle();
    }
  };

  const handleDownload = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert("ไม่สามารถดาวน์โหลดไฟล์ได้");
    }
  };

  const formatFileSize = (bytes) => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 Byte";
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered={true} fade={true} className="modal-left" size="lg">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <div className="modal-des">{desc}</div>

        <div className="modal-child !max-h-none">
          <input
            type="file"
            key={file ? "has-file" : "no-file"}
            onChange={handleInputChange}
            accept={accept
              .split(",")
              .map((ext) => `.${ext.trim().toLowerCase()}`)
              .join(",")}
            style={{ display: "none" }}
            id="file-input"
          />
          <div
            {...getRootProps()}
            className={`dropzone-upload ${isDragActive ? "active" : ""} ${file?.url ? "has-image" : ""}`}
            style={{
              minHeight: "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
              border: isDragActive ? "2px dashed #3b82f6" : "2px dashed #d1d5db",
              borderRadius: "0.5rem",
              cursor: "pointer",
              background: file?.url ? `url(${file.url}) center/contain no-repeat` : "",
              transition: "none",
            }}
          >
            <input {...getInputProps()} />
            {!file?.url ? (
              <label
                htmlFor="file-input"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  padding: "2rem",
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                }}
              >
                <ImageIcon style={{ width: 80, height: 80, marginBottom: "1rem", color: "#9ca3af" }} />
                <h3 className="mb-2 fw-semibold">{message}</h3>
                <div className="text-muted small">
                  <h6 className="text-primary-600">รองรับชนิดไฟล์: {accept}</h6>
                  <span className="text-md">ขนาดไฟล์สูงสุด: {formatFileSize(maxSize)}</span>
                </div>
              </label>
            ) : (
              <div
                style={{
                  position: "absolute",
                  bottom: "1rem",
                  right: "1rem",
                  display: "flex",
                  gap: "0.5rem",
                }}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(file.url, file.name);
                  }}
                >
                  <DownloadIcon style={{ width: 16, height: 16, marginRight: "0.25rem" }} />
                  ดาวน์โหลด
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                >
                  <TrashIcon style={{ width: 16, height: 16, marginRight: "0.25rem" }} />
                  ลบ
                </button>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      {!immediateAction && (
      <ModalFooter className="justify-content-between">
        <Button
          className="w-100 max-w-52" 
          color="light"
          onClick={handleCancel}
          >
          ยกเลิก
        </Button>
        <Button 
          className="w-100 max-w-52" 
          color="primary" 
          onClick={handleConfirm} 
          disabled={!file || file.status !== "completed"}
        >
         ยืนยัน
        </Button>
      </ModalFooter>
      )}
    </Modal>
  );
};

export default UploadImage;
